/**
 * @description Main container for the Company > Scheduling tab
 *
 * Key features:
 * - Sub-navigation: Schedule | Time Entries
 * - Permission-gated views
 * - Schedule: Employee shift calendar (uses schedule category events)
 * - Time Entries: Filterable list of clock entries with edit/create/export
 *
 * @dependencies
 * - ShiftScheduleView: Employee shift management
 * - TimeEntryList: Time entry management
 * - PermissionGate / usePermissions: time_clock.view_all for Time Entries
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import TimeEntryList from './TimeEntryList';
import ShiftScheduleView from './ShiftScheduleView';
import { usePermissions } from '../../../contexts/PermissionContext';
import { PERMISSIONS } from '../../../constants/permissionMappings';

const CompanySchedulingTab = () => {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canViewTimeEntries = hasPermission(PERMISSIONS.time_clock.view_all);
  const [activeView, setActiveView] = useState('schedule');

  // Handle view switching from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    if (viewParam === 'timeEntries' && !canViewTimeEntries) {
      setActiveView('schedule');
      return;
    }
    if (viewParam && ['schedule', 'timeEntries'].includes(viewParam)) {
      setActiveView(viewParam);
    }
  }, [location.search, canViewTimeEntries]);

  const views = [
    { key: 'schedule', label: 'Schedule', icon: Calendar },
    ...(canViewTimeEntries
      ? [{ key: 'timeEntries', label: 'Time Entries', icon: Clock }]
      : []),
  ];

  const renderView = () => {
    switch (activeView) {
      case 'schedule':
        return <ShiftScheduleView />;
      case 'timeEntries':
        return canViewTimeEntries ? <TimeEntryList /> : <ShiftScheduleView />;
      default:
        return <ShiftScheduleView />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header & Navigation */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm">
        <nav className="flex flex-wrap items-center justify-center md:justify-start gap-1 w-full md:w-auto">
          {views.map((view) => {
            const Icon = view.icon;
            const isActive = activeView === view.key;

            return (
              <button
                key={view.key}
                onClick={() => setActiveView(view.key)}
                className={`
                  flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-200
                  ${isActive
                    ? 'bg-gray-50 text-gray-900 shadow-sm border border-gray-200 ring-1 ring-black/5'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50/50'
                  }
                `}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-brand' : 'text-gray-400'}`} />
                <span className="hidden sm:inline">{view.label}</span>
                <span className="sm:hidden">{view.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* View Content */}
      <div>
        {renderView()}
      </div>
    </div>
  );
};

export default CompanySchedulingTab;
